/**
 * Backend earningFrequency enum — values must match RewardEvent schema exactly.
 * Confirmed from API examples: ONCE, UNLIMITED, COOLDOWN.
 *
 * Daily/monthly caps use separate fields (backend enforces reset + monthly hard stop):
 * - dailyLimit: max per calendar day (resets daily)
 * - monthlyLimit: max per calendar month (hard cap; blocks even if daily resets)
 * earningFrequency is not sent for daily/monthly mode — limits alone define the rule.
 */

export const EARNING_FREQUENCY = {
  ONCE: "ONCE",
  UNLIMITED: "UNLIMITED",
  COOLDOWN: "COOLDOWN",
};

/** UI dropdown keys — only earningFrequency values go on the wire */
export const UI_FREQUENCY = {
  ONCE: "ONCE",
  UNLIMITED: "UNLIMITED",
  LIMITED: "LIMITED",
  COOLDOWN: "COOLDOWN",
};

export const UI_FREQUENCY_LABELS = {
  [UI_FREQUENCY.ONCE]: "One-time only",
  [UI_FREQUENCY.UNLIMITED]: "Unlimited",
  [UI_FREQUENCY.LIMITED]: "Daily / monthly limit",
  [UI_FREQUENCY.COOLDOWN]: "Cooldown period",
};

/** Payload-only frequencies — never shown as separate dropdown options */
const PAYLOAD_ONLY_FREQUENCIES = new Set(["MONTHLY", "DAILY"]);

/** Events whose frequency/limits are fixed in backend — admin UI is read-only */
export const BACKEND_HARDCODED_FREQUENCY_EVENT_KEYS = new Set(["APP_LAUNCH_STREAK"]);

export function isBackendHardcodedFrequencyEvent(eventKey) {
  return BACKEND_HARDCODED_FREQUENCY_EVENT_KEYS.has(eventKey);
}

export const DEFAULT_UI_FREQUENCY_OPTIONS = [
  { uiKey: UI_FREQUENCY.ONCE, apiFrequency: EARNING_FREQUENCY.ONCE, limits: "none" },
  { uiKey: UI_FREQUENCY.UNLIMITED, apiFrequency: EARNING_FREQUENCY.UNLIMITED, limits: "none" },
  {
    uiKey: UI_FREQUENCY.LIMITED,
    limits: "daily_monthly",
  },
  { uiKey: UI_FREQUENCY.COOLDOWN, apiFrequency: EARNING_FREQUENCY.COOLDOWN, limits: "cooldown" },
];

export function getUiFrequencyKey(cfg) {
  const limits = inferLimitsFromEvent(cfg);
  if (limits === "daily" || limits === "monthly" || limits === "daily_monthly") {
    return UI_FREQUENCY.LIMITED;
  }

  const freq = (cfg.earningFrequency || EARNING_FREQUENCY.ONCE).toUpperCase();

  if (freq === EARNING_FREQUENCY.ONCE) return UI_FREQUENCY.ONCE;
  if (freq === EARNING_FREQUENCY.COOLDOWN) return UI_FREQUENCY.COOLDOWN;
  if (freq === EARNING_FREQUENCY.UNLIMITED) return UI_FREQUENCY.UNLIMITED;
  if (PAYLOAD_ONLY_FREQUENCIES.has(freq)) return UI_FREQUENCY.LIMITED;

  return freq;
}

export function getUiFrequencyOption(uiKey, options = DEFAULT_UI_FREQUENCY_OPTIONS) {
  return options.find((opt) => opt.uiKey === uiKey);
}

export function getLimitsModeForUiKey(uiKey, options = DEFAULT_UI_FREQUENCY_OPTIONS) {
  return getUiFrequencyOption(uiKey, options)?.limits || "none";
}

export function getLimitsMode(cfg, options = DEFAULT_UI_FREQUENCY_OPTIONS) {
  return getLimitsModeForUiKey(getUiFrequencyKey(cfg), options);
}

export function inferLimitsFromEvent(event) {
  // Prefer explicit limit fields over a stale earningFrequency (e.g. switching
  // Cooldown → Daily/monthly clears cooldownDays but may leave COOLDOWN on the cfg).
  if (event.cooldownDays != null) {
    return "cooldown";
  }
  if (event.dailyLimit != null && event.monthlyLimit != null) return "daily_monthly";
  if (event.dailyLimit != null) return "daily";
  if (event.monthlyLimit != null) return "monthly";
  if (event.earningFrequency === EARNING_FREQUENCY.COOLDOWN) {
    return "cooldown";
  }
  return "none";
}

export function buildFrequencyOptionsFromEvents(events) {
  const options = DEFAULT_UI_FREQUENCY_OPTIONS.map((opt) => ({ ...opt }));
  const knownApi = new Set(options.map((opt) => opt.apiFrequency).filter(Boolean));

  events.forEach((event) => {
    const freq = (event.earningFrequency || "").toUpperCase();
    if (!freq || knownApi.has(freq) || PAYLOAD_ONLY_FREQUENCIES.has(freq)) return;

    knownApi.add(freq);
    if (!options.some((opt) => opt.uiKey === freq)) {
      options.push({
        uiKey: freq,
        apiFrequency: freq,
        limits: inferLimitsFromEvent(event),
        label: UI_FREQUENCY_LABELS[freq] || freq,
      });
    }
  });

  return options;
}
